import { SupportedChain, SUPPORTED_CHAINS } from '../constants';
import { isChainSupported } from '../config/chains';
import {
  ChainAdapter,
  TransactionResult,
  WalletInfo,
  TicketInfo,
  GroupInfo,
  JackpotInfo,
  WhitelistInfo,
  GroupIdentifier,
} from './interfaces/ChainAdapter';

// Adapter imports
import { BNBAdapter } from './adapters/BNBAdapter';
import { SolanaAdapter } from './adapters/SolanaAdapter';

// Helper for development-only logging
const devLog = (...args: any[]) => {
  if (process.env.NODE_ENV !== 'production') {
    console.log('[ChainManager]', ...args);
  }
};

class ChainManager {
  private currentChain: SupportedChain;
  private adapters: Map<SupportedChain, ChainAdapter> = new Map();
  private listeners: Map<string, ((...args: any[]) => void)[]> = new Map();
  private adapterUnsubscribers: Map<SupportedChain, (() => void)[]> = new Map();

  constructor(initialChain: SupportedChain = 'bnb') {
    this.currentChain = initialChain;
    this.initializeAdapters();
    this.loadPersistedChain();
    devLog('Initialized with chain:', this.currentChain);
  }

  private initializeAdapters(): void {
    // Initialize BNB adapter
    const bnbAdapter = new BNBAdapter();
    this.adapters.set('bnb', bnbAdapter);

    // Initialize Solana adapter
    const solanaAdapter = new SolanaAdapter();
    this.adapters.set('solana', solanaAdapter);

    // Set up adapter event listeners
    this.setupAdapterEventListeners();
    devLog('Adapters initialized');
  }

  private setupAdapterEventListeners(): void {
    // Clean up existing listeners first
    this.cleanupAdapterListeners();

    // Set up BNB adapter listeners
    const bnbAdapter = this.adapters.get('bnb');
    if (bnbAdapter) {
      const unsubscribe = bnbAdapter.onWalletChange(walletInfo => {
        this.emit('walletChanged', walletInfo);
      });

      if (!this.adapterUnsubscribers.has('bnb')) {
        this.adapterUnsubscribers.set('bnb', []);
      }
      this.adapterUnsubscribers.get('bnb')?.push(unsubscribe);
    }

    // Set up Solana adapter listeners
    const solanaAdapter = this.adapters.get('solana');
    if (solanaAdapter) {
      const unsubscribe = solanaAdapter.onWalletChange(walletInfo => {
        this.emit('walletChanged', walletInfo);
      });

      if (!this.adapterUnsubscribers.has('solana')) {
        this.adapterUnsubscribers.set('solana', []);
      }
      this.adapterUnsubscribers.get('solana')?.push(unsubscribe);
    }
  }

  private cleanupAdapterListeners(): void {
    this.adapterUnsubscribers.forEach((unsubscribers, chain) => {
      unsubscribers.forEach(unsubscribe => {
        try {
          unsubscribe();
        } catch (error) {
          console.warn(`Failed to cleanup listener for ${chain}:`, error);
        }
      });
    });
    this.adapterUnsubscribers.clear();
  }

  private loadPersistedChain(): void {
    try {
      const persisted = localStorage.getItem('lixinft-current-chain');
      if (persisted && isChainSupported(persisted)) {
        this.currentChain = persisted;
      }
    } catch (error) {
      console.warn('Failed to load persisted chain:', error);
    }
  }

  private persistChain(): void {
    try {
      localStorage.setItem('lixinft-current-chain', this.currentChain);
    } catch (error) {
      console.warn('Failed to persist chain:', error);
    }
  }

  // Chain management
  getCurrentChain(): SupportedChain {
    return this.currentChain;
  }

  getSupportedChains(): SupportedChain[] {
    return [...SUPPORTED_CHAINS];
  }

  async switchChain(chain: SupportedChain): Promise<void> {
    if (!isChainSupported(chain)) {
      throw new Error(`Unsupported chain: ${chain}`);
    }

    const currentAdapter = this.getCurrentAdapter();

    // Disconnect from current chain if connected
    if (currentAdapter && (await currentAdapter.isWalletConnected())) {
      await currentAdapter.disconnectWallet();
      await new Promise(resolve => setTimeout(resolve, 200));
    }

    // Clear wallet state before switching
    this.emit('walletChanged', null);

    // Switch to new chain
    this.currentChain = chain;
    this.persistChain();

    // Emit chain change event
    this.emit('chainChanged', chain);
    await new Promise(resolve => setTimeout(resolve, 150));
  }

  getCurrentAdapter(): ChainAdapter {
    const adapter = this.adapters.get(this.currentChain);
    if (!adapter) {
      throw new Error(`No adapter found for chain: ${this.currentChain}`);
    }
    return adapter;
  }

  getAdapter(chain: SupportedChain): ChainAdapter {
    const adapter = this.adapters.get(chain);
    if (!adapter) {
      throw new Error(`No adapter found for chain: ${chain}`);
    }
    return adapter;
  }

  // Event system
  on(event: string, callback: (...args: any[]) => void): () => void {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event)?.push(callback);

    return () => {
      const listeners = this.listeners.get(event);
      if (listeners) {
        const index = listeners.indexOf(callback);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  private emit(event: string, ...args: any[]): void {
    const listeners = this.listeners.get(event);
    if (listeners) {
      listeners.forEach(callback => {
        try {
          callback(...args);
        } catch (error) {
          console.error(`Error in event listener for ${event}:`, error);
        }
      });
    }
  }

  // Unified methods that delegate to current adapter
  async connectWallet(): Promise<WalletInfo> {
    const adapter = this.getCurrentAdapter();

    try {
      const walletInfo = await adapter.connectWallet();
      return walletInfo;
    } catch (error) {
      console.error('Wallet connection failed:', error);
      this.emit('walletError', error);
      throw error;
    }
  }

  async disconnectWallet(): Promise<void> {
    const adapter = this.getCurrentAdapter();
    return adapter.disconnectWallet();
  }

  async getWalletInfo(): Promise<WalletInfo | null> {
    const adapter = this.getCurrentAdapter();
    return adapter.getWalletInfo();
  }

  async isWalletConnected(): Promise<boolean> {
    const adapter = this.getCurrentAdapter();
    return adapter.isWalletConnected();
  }

  async signMessage(): Promise<string> {
    const adapter = this.getCurrentAdapter();
    return adapter.signMessage();
  }

  // Address validation
  validateAddress(address: string): boolean {
    const adapter = this.getCurrentAdapter();
    return adapter.validateAddress(address);
  }

  // Core transactions
  async buyTickets(params: {
    group: GroupIdentifier;
    quantity: number;
    price: string;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().buyTickets(params);
  }

  async claimLixi(params: {
    group: GroupIdentifier;
    ticketId?: string;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().claimLixi(params);
  }

  async claimLixiByOwner(params: {
    group: GroupIdentifier;
    receiver: string;
  }): Promise<TransactionResult> {
    const adapter = this.getCurrentAdapter();
    if (!adapter.claimLixiByOwner) {
      throw new Error(`claimLixiByOwner not supported on ${this.currentChain}`);
    }
    return adapter.claimLixiByOwner(params);
  }

  async claimManyByOwner(params: {
    group: GroupIdentifier;
    receivers: string[];
  }): Promise<TransactionResult> {
    const adapter = this.getCurrentAdapter();
    if (!adapter.claimManyByOwner) {
      throw new Error(`claimManyByOwner not supported on ${this.currentChain}`);
    }
    return adapter.claimManyByOwner(params);
  }

  async claimLixiByAdmin(params: {
    group: GroupIdentifier;
    receiver: string;
  }): Promise<TransactionResult> {
    const adapter = this.getCurrentAdapter();
    if (!adapter.claimLixiByAdmin) {
      throw new Error(`claimLixiByAdmin not supported on ${this.currentChain}`);
    }
    return adapter.claimLixiByAdmin(params);
  }

  async claimManyByAdmin(params: {
    group: GroupIdentifier;
    receivers: string[];
  }): Promise<TransactionResult> {
    const adapter = this.getCurrentAdapter();
    if (!adapter.claimManyByAdmin) {
      throw new Error(`claimManyByAdmin not supported on ${this.currentChain}`);
    }
    return adapter.claimManyByAdmin(params);
  }

  async claimGlobalJackpot(): Promise<TransactionResult> {
    return this.getCurrentAdapter().claimGlobalJackpot();
  }

  async claimLocalJackpot(params: {
    group: GroupIdentifier;
    groupId: string;
  }): Promise<TransactionResult> {
    const adapter = this.getCurrentAdapter();
    if (!adapter.claimLocalJackpot) {
      throw new Error(
        `claimLocalJackpot not supported on ${this.currentChain}`,
      );
    }
    return adapter.claimLocalJackpot(params);
  }

  async claimFundBack(params: {
    group: GroupIdentifier;
    groupId: string;
  }): Promise<TransactionResult> {
    const adapter = this.getCurrentAdapter();
    if (!adapter.claimFundBack) {
      throw new Error(`claimFundBack not supported on ${this.currentChain}`);
    }
    return adapter.claimFundBack(params);
  }

  // Whitelist management
  async addWhitelister(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().addWhitelister(params);
  }

  async removeWhitelister(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().removeWhitelister(params);
  }

  async addManyWhitelisters(params: {
    group: GroupIdentifier;
    users: string[];
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().addManyWhitelisters(params);
  }

  // Configuration methods
  async setTicketPrice(params: {
    group: GroupIdentifier;
    price: string;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().setTicketPrice(params);
  }

  async setClosingTime(params: {
    group: GroupIdentifier;
    closingTime: number;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().setClosingTime(params);
  }

  async setAdminStatus(params: {
    account: string;
    isAdmin: boolean;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().setAdminStatus(params);
  }

  async setJackpotConfig(params: {
    config: unknown;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().setJackpotConfig(params);
  }

  async setWinningHash(params: {
    group: GroupIdentifier;
    hash: string;
  }): Promise<TransactionResult> {
    return this.getCurrentAdapter().setWinningHash(params);
  }

  // View methods
  async getTicketInfo(ticketId: string): Promise<TicketInfo | null> {
    return this.getCurrentAdapter().getTicketInfo(ticketId);
  }

  async getGroupInfo(group: GroupIdentifier): Promise<GroupInfo | null> {
    return this.getCurrentAdapter().getGroupInfo(group);
  }

  async getGlobalJackpotPrice(): Promise<JackpotInfo | null> {
    return this.getCurrentAdapter().getGlobalJackpotPrice();
  }

  async isWhitelisted(params: {
    group: GroupIdentifier;
    user: string;
  }): Promise<WhitelistInfo | null> {
    return this.getCurrentAdapter().isWhitelisted(params);
  }

  // SPL Token methods (Solana specific)
  async getSPLTokenInfo(mintAddress: string): Promise<any> {
    const adapter = this.getCurrentAdapter();
    return adapter.getSPLTokenInfo?.(mintAddress) || null;
  }

  async getSPLTokenBalance(
    mintAddress: string,
    userAddress?: string,
  ): Promise<number> {
    const adapter = this.getCurrentAdapter();
    return adapter.getSPLTokenBalance?.(mintAddress, userAddress) || 0;
  }

  async getCommonSPLTokens(): Promise<Record<string, any>> {
    const adapter = this.getCurrentAdapter();
    return adapter.getCommonSPLTokens?.() || {};
  }

  async approveSPLToken(
    mintAddress: string,
    amount: number,
  ): Promise<TransactionResult> {
    const adapter = this.getCurrentAdapter();
    if (!adapter.approveSPLToken) {
      return {
        success: false,
        error: 'SPL token approval not supported on this chain',
      };
    }
    return adapter.approveSPLToken(mintAddress, amount);
  }

  // Connection method (Solana specific)
  getConnection(): any {
    const adapter = this.getCurrentAdapter();
    return (adapter as any).getConnection?.() || null;
  }

  // Global state method (Solana specific)
  async getGlobalState(): Promise<any> {
    const adapter = this.getCurrentAdapter();
    return (adapter as any).getGlobalState?.() || null;
  }

  // Group state method (Solana specific)
  async getGroupState(groupId: number): Promise<any> {
    const adapter = this.getCurrentAdapter();
    return (adapter as any).getGroupState?.(groupId) || null;
  }

  // Create buy tickets instruction method (Solana specific)
  async createBuyTicketsInstruction(params: {
    groupId: string;
    quantity: number;
    price: string;
    extraTokenMint?: string;
    extraTokenAmount?: number;
    receivers?: string[];
  }): Promise<any> {
    const adapter = this.getCurrentAdapter();
    return (adapter as any).createBuyTicketsInstruction?.(params) || null;
  }

  // Cleanup method to prevent memory leaks
  destroy(): void {
    this.cleanupAdapterListeners();
    this.listeners.clear();
  }
}

// Singleton instance
export const chainManager = new ChainManager();
export default chainManager;
